import React, { useEffect, useMemo } from "react";

import { orderBy } from "lodash";
import { useHistory, useParams } from "react-router-dom";

import { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useGetGoalBasedFinancialPlans from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlans";
import { GoalBasedFinancialPlanStatus } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { FinancialPlansPath } from "@src/config/routing/RouterPaths";
import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";

import FinancialPlansDashboard from "./FinancialPlansDashboard";

const FinancialPlans = (): JSX.Element => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId | undefined;
  }>();

  const history = useHistory();

  const auth = useRequestAuth();

  const { Incomplete, Complete } = GoalBasedFinancialPlanStatus.Enum;

  const {
    data: goalBasedFinancialPlans = [],
    isLoading: isLoadingFinancialPlans,
  } = useGetGoalBasedFinancialPlans(clientId, auth);

  const { completedPlans, drafts, favorite } = useMemo(() => {
    if (goalBasedFinancialPlans === undefined) {
      return {
        completedPlans: [],
        drafts: [],
        favorite: null,
      };
    }

    const drafts = goalBasedFinancialPlans.filter(
      (p) => p.status === Incomplete
    );

    return {
      completedPlans: goalBasedFinancialPlans.filter(
        (p) => p.status === Complete
      ),
      drafts: orderBy(drafts, [(d) => d.name], "asc"),
      favorite: goalBasedFinancialPlans.find((p) => p.isFavorite) ?? null,
    };
  }, [goalBasedFinancialPlans, Incomplete, Complete]);

  useEffect(() => {
    if (
      goalBasedFinancialPlans !== undefined &&
      goalBasedFinancialPlanId === undefined &&
      favorite !== null
    ) {
      history.push(
        `/Client/${clientId}/${FinancialPlansPath}/Dashboard/${favorite.goalBasedFinancialPlanId}`
      );
    }
  }, [
    history,
    clientId,
    goalBasedFinancialPlanId,
    goalBasedFinancialPlans,
    favorite,
  ]);

  if (isLoadingFinancialPlans) {
    return (
      <BaseLayout>
        <LogoLoadingStill onTop />
      </BaseLayout>
    );
  }

  return (
    <FinancialPlansDashboard
      completedPlans={completedPlans}
      drafts={drafts}
      favorite={favorite}
    />
  );
};

export default FinancialPlans;
